import React, { useState } from 'react';
import { Button, Paper, Typography, Alert, CircularProgress, Box } from '@mui/material';
import { Download, FileDownload } from '@mui/icons-material';
import { exportTrainingSamples } from '../services/trainingExport';
import { useAuth } from '../contexts/AuthContext';

const TrainingSampleExport: React.FC = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleExport = async (format: 'json' | 'csv') => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await exportTrainingSamples(format);
      
      if (format === 'csv') {
        const blob = result as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `training-samples-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess('CSV file downloaded successfully');
      } else {
        const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `training-samples-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess('JSON file downloaded successfully');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Alert severity="error">
        You must be signed in to access this page.
      </Alert>
    );
  }

  if (!isAdmin) {
    return (
      <Alert severity="error">
        You do not have permission to access this page. Admin access required.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Training Sample Export
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Export validated training samples (only samples with labels).
      </Typography>

      <Paper sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <Download />}
            onClick={() => handleExport('json')}
            disabled={loading}
          >
            Export as JSON
          </Button>

          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <FileDownload />}
            onClick={() => handleExport('csv')}
            disabled={loading}
          >
            Export as CSV
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default TrainingSampleExport;